import { WebhookEvent } from '@/types/TReseller';

export type WebhookEventPayload = {
	id: string;
	name: string;
	description: string;
	apiPayload: Record<string, any>;
	slackPayload: Record<string, any>;
};

export const webhookEvents: WebhookEventPayload[] = [
	{
		id: '1',
		name: WebhookEvent.CLIENT_CREATED,
		description: 'Triggered when a client is created. This is for both trial and paid clients.',
		apiPayload: {
			event: WebhookEvent.CLIENT_CREATED,
			data: {
				username: 'string',
				email: 'string',
				businessName: 'string',
				businessType: 'string',
				registeredAt: 'string',
				isTrial: 'boolean',
			},
		},
		slackPayload: {
			text: '🎉 *New Client Registration*',
			blocks: [
				{
					type: 'section',
					text: {
						type: 'mrkdwn',
						text: '🎉 *New Client Registration*\n\n*Username:* {{username}}\n*Email:* {{email}}\n*Business Name:* {{businessName}}\n*Business Type:* {{businessType}}',
					},
				},
				{
					type: 'divider',
				},
				{
					type: 'context',
					elements: [
						{
							type: 'mrkdwn',
							text: 'Registered at: {{registeredAt}}',
						},
					],
				},
			],
		},
	},
	{
		id: '2',
		name: WebhookEvent.CLIENT_PAYMENT_FAILED,
		description: 'Triggered when a client payment fails.',
		apiPayload: {
			event: WebhookEvent.CLIENT_PAYMENT_FAILED,
			data: {
				username: 'string',
				email: 'string',
				businessName: 'string',
				attemptNumber: 'number',
				subscriptionId: 'string',
				customerId: 'string',
				failedAt: 'string',
			},
		},
		slackPayload: {
			text: '❌ *Monthy Payment Failure Alert for {{username}}*',
			blocks: [
				{
					type: 'section',
					text: {
						type: 'mrkdwn',
						text: '❌ *Client Payment Failure Alert*',
					},
				},
				{
					type: 'section',
					fields: [
						{
							type: 'mrkdwn',
							text: '*Username:* {{username}}',
						},
						{
							type: 'mrkdwn',
							text: '*Business Name:* {{businessName}}',
						},
						{
							type: 'mrkdwn',
							text: '*Attempt Number:* {{attemptNumber}}',
						},
					],
				},
				{
					type: 'context',
					elements: [
						{
							type: 'mrkdwn',
							text: 'Payment failure occurred at: {{failedAt}}',
						},
					],
				},
			],
		},
	},
	{
		id: '3',
		name: WebhookEvent.TRIAL_ACCOUNT_CONVERTED,
		description: 'Triggered when a trial account is converted to a paid account.',
		apiPayload: {
			event: WebhookEvent.TRIAL_ACCOUNT_CONVERTED,
			data: {
				username: 'string',
				email: 'string',
				businessName: 'string',
				planName: 'string',
				price: 'string',
				convertedAt: 'string',
			},
		},
		slackPayload: {
			text: '🎉 *Trial Conversion Alert*',
			blocks: [
				{
					type: 'section',
					text: {
						type: 'mrkdwn',
						text: '🎉 *Client Converted from Trial to Paid Account*',
					},
				},
				{
					type: 'section',
					fields: [
						{
							type: 'mrkdwn',
							text: '*Username:* {{username}}',
						},
						{
							type: 'mrkdwn',
							text: '*Business Name:* {{businessName}}',
						},
						{
							type: 'mrkdwn',
							text: '*Plan:* {{planName}}',
						},
						{
							type: 'mrkdwn',
							text: '*Price:* {{price}}',
						},
					],
				},
				{
					type: 'context',
					elements: [
						{
							type: 'mrkdwn',
							text: 'Converted at: {{convertedAt}}',
						},
					],
				},
			],
		},
	},
	{
		id: '4',
		name: WebhookEvent.CLIENT_CHURNED,
		description: 'Triggered when a client churns.',
		apiPayload: {
			event: WebhookEvent.CLIENT_CHURNED,
			data: {
				username: 'string',
				email: 'string',
				canceledAt: 'string',
			},
		},
		slackPayload: {
			text: '🚨 *Subscription Canceled*',
			blocks: [
				{
					type: 'section',
					text: {
						type: 'mrkdwn',
						text: '🚨 *Subscription Canceled*\n\n *Username:* {{username}} \n*Email:* {{email}}',
					},
				},
				{
					type: 'divider',
				},
				{
					type: 'context',
					elements: [
						{
							type: 'mrkdwn',
							text: 'Canceled at: {{canceledAt}}',
						},
					],
				},
			],
		},
	},
	{
		id: '5',
		name: WebhookEvent.CALL_MADE,
		description: 'Triggered when a call is made by any client.',
		apiPayload: {
			event: WebhookEvent.CALL_MADE,
			data: {
				username: 'string',
				email: 'string',
				duration: 'number',
				from: 'string',
				to: 'string',
				timestamp: 'string',
				callSid: 'string',
			},
		},
		slackPayload: {
			text: '📞 *Call Alert*',
			blocks: [
				{
					type: 'section',
					text: {
						type: 'mrkdwn',
						text: '📞 *Client Call Alert*',
					},
				},
				{
					type: 'section',
					fields: [
						{
							type: 'mrkdwn',
							text: '*Username:* {{username}}',
						},
						{
							type: 'mrkdwn',
							text: '*Business Name:* {{businessName}}',
						},
						{
							type: 'mrkdwn',
							text: '*Call SID:* {{callSid}}',
						},
						{
							type: 'mrkdwn',
							text: '*Call Duration:* {{callDuration}}',
						},
						{
							type: 'mrkdwn',
							text: '*Call From:* {{callFrom}}',
						},
						{
							type: 'mrkdwn',
							text: '*Call To:* {{callTo}}',
						},
					],
				},
				{
					type: 'context',
					elements: [
						{
							type: 'mrkdwn',
							text: 'Call occurred at: {{callOccurredAt}}',
						},
					],
				},
			],
		},
	},
	{
		id: '6',
		name: WebhookEvent.TEXT_MADE,
		description: 'Triggered when a text message is sent by any client.',
		apiPayload: {
			event: WebhookEvent.TEXT_MADE,
			data: {
				username: 'string',
				from: 'string',
				to: 'string',
				messageSid: 'string',
				timestamp: 'string',
				credits: 'number',
			},
		},
		slackPayload: {
			text: '📱 *Text Message Alert*',
			blocks: [
				{
					type: 'section',
					text: {
						type: 'mrkdwn',
						text: '📱 *Client Text Message Alert*',
					},
				},
				{
					type: 'section',
					fields: [
						{
							type: 'mrkdwn',
							text: '*Username:* {{username}}',
						},
						{
							type: 'mrkdwn',
							text: '*Business Name:* {{businessName}}',
						},
						{
							type: 'mrkdwn',
							text: '*From:* {{from}}',
						},
						{
							type: 'mrkdwn',
							text: '*To:* {{to}}',
						},
					],
				},
				{
					type: 'context',
					elements: [
						{
							type: 'mrkdwn',
							text: 'Text message sent at: {{timestamp}}',
						},
					],
				},
			],
		},
	},
];
